import { useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { CreditBalance } from '~/features/billing/CreditBalance/CreditBalance';
import { useClientId } from '~/hooks/useClientId';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { CreditsLedger } from './CreditsLedger';
import { OneTimeCreditPanel } from './OneTimeCreditPanel';
import { RecurringCreditsPanel } from './RecurringCreditsPanel';

const Page = () => {
  const intl = useIntl();
  const { location, params } = useRouting();
  const {
    data: creditSummaryData,
    isFetching: isCreditSummaryFetching,
    isError: isCreditSummaryError,
  } = api.endpoints.getCreditSummary.useQueryState(Number(params.clientId));
  // TODO: M1.5 We will only display active balances but M2 will use a separate endpoint.
  // const {
  //   data = { transactions: [], totalCount: 0 },
  //   isError,
  //   isFetching,
  // } = api.endpoints.getCreditLedger.useQuery(clientId);

  const [panelName, setPanelName] = useState<Nullable<string>>(null);
  const [balanceId, setBalanceId] = useState<Nullable<Uuid>>(null);
  const clientId = useClientId();
  const { getSearchParam } = useRouting();
  const pageIndex = Number(getSearchParam('pageIndex') ?? 1);

  const handlePanelClose = () => setPanelName(null);

  const placeholderContent = isCreditSummaryError
    ? {
        message: intl.formatMessage({
          id: 'CreditsPage.loadingErrorMessage',
          defaultMessage:
            'There was an error loading credit transaction. Refresh the page to try again.',
          description: 'Error message when credit transactions fail to load',
        }),
      }
    : creditSummaryData?.balances === undefined ||
        (creditSummaryData.balances && creditSummaryData.balances.length === 0)
      ? {
          title: intl.formatMessage({
            id: 'CreditsPage.noTransactionsTitle',
            defaultMessage: 'No Transactions',
            description: 'Title when no credit transactions exist',
          }),
          message: intl.formatMessage({
            id: 'CreditsPage.noTransactionsMessage',
            defaultMessage:
              'No credit transactions have not been made in this client.',
            description: 'Message when no credit transactions exist',
          }),
        }
      : undefined;

  const setPanel = (panelName: string, id: Nullable<Uuid> = null) => {
    setPanelName(panelName);
    setBalanceId(id);
  };

  const activeBalance = creditSummaryData?.balances?.find(
    (balance) => balance.id === balanceId,
  );

  return (
    <>
      <PageLayout
        actions={[
          {
            text: intl.formatMessage({
              id: 'CreditsPage.addOneTimeCreditsButton',
              defaultMessage: 'Add One-Time Credits',
              description: 'Button text to add one-time credits',
            }),
            onClick: () => setPanel('one-time-credits'),
          },
          {
            text: intl.formatMessage({
              id: 'CreditsPage.manageRecurringCreditsButton',
              defaultMessage: 'Manage Recurring Credits',
              description: 'Button text to manage recurring credits',
            }),
            onClick: () => setPanel('recurring-credits'),
          },
        ]}
        headerCalloutContent={
          <CreditBalance clientId={Number(params.clientId)} />
        }
        loadingContent={{
          isLoading: isCreditSummaryFetching,
          message: intl.formatMessage({
            id: 'CreditsPage.loadingTransactionsMessage',
            defaultMessage: 'Loading transactions...',
            description: 'Loading message while transactions are being fetched',
          }),
        }}
        nav={{
          actions: [
            {
              text: intl.formatMessage({
                id: 'CreditsPage.adminConsoleNavText',
                defaultMessage: 'Admin Console',
                description: 'Navigation text for admin console',
              }),
              pathname: generatePath(RoutePathType.AdminConsoleClient, {
                clientId,
              }),
            },
            {
              text: intl.formatMessage({
                id: 'CreditsPage.adminNavText',
                defaultMessage: 'Admin',
                description: 'Navigation text for admin',
              }),
              pathname: RoutePathType.Admin,
            },
            {
              text:
                (location.state as { clientName: string })?.clientName ??
                intl.formatMessage({
                  id: 'CreditsPage.clientNavFallbackText',
                  defaultMessage: 'Client',
                  description: 'Fallback navigation text for client',
                }),
              pathname: RoutePathType.AdminClientCredits,
            },
          ],
        }}
        placeholderContent={placeholderContent}
        title={intl.formatMessage({
          id: 'CreditsPage.pageTitle',
          defaultMessage: 'View Credits',
          description: 'Page title for viewing credits',
        })}
      >
        {creditSummaryData?.balances &&
          creditSummaryData.balances.length > 0 && (
            <CreditsLedger
              pageIndex={pageIndex}
              totalCount={creditSummaryData?.balances?.length ?? 0}
              transactions={creditSummaryData?.balances ?? []}
              setPanel={setPanel}
            />
          )}
      </PageLayout>
      {panelName === 'one-time-credits' && (
        <OneTimeCreditPanel
          balance={activeBalance}
          onClose={handlePanelClose}
        />
      )}
      {panelName === 'recurring-credits' && creditSummaryData && (
        <RecurringCreditsPanel
          creditSummary={creditSummaryData}
          onClose={handlePanelClose}
        />
      )}
    </>
  );
};

export default Page;
